import axios from 'axios';
import { adminConfig } from '../config/firebase-config';

export interface Conversation {
  id: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
  messageCount: number;
  status: 'active' | 'ended';
  conversationType?: 'chat' | 'trip_status' | 'check_availability'; // Add conversation type
  initialButtonType?: string; // Add initial button type
  hasChatSubmission?: boolean; // True when a real user chat message was submitted (not just button clicks)
  sessionInfo?: {
    userAgent: string;
    deviceType: string;
    referrer: string;
    ipHash: string;
  };
  lastMessage?: string;
  firstMessage?: string;
  messagePreview?: {
    userMessage: string;
    aiResponse: string;
    timestamp: Date;
  };
  initialStatusMessage?: string;
}

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  conversationId: string;
  metadata?: {
    ragSources?: Array<{
      title: string;
      tier: number;
      category: string;
      similarity: number;
    }>;
    model?: string;
    tokens?: number;
    availabilityAction?: {
      openChecker: boolean;
      date: string | null;
      filter: string;
    };
  };
  actions?: Array<{
    label: string;
    type: string;
    url?: string;
    data?: any;
  }>;
}

class ConversationsService {
  private getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': 'geoff@emertech.nz' // TODO: Replace with real auth
    };
  }

  async getAllConversations(limit: number = 50, offset: number = 0): Promise<{ conversations: Conversation[]; total: number; hasMore: boolean }> {
    try {
      const apiUrl = `${adminConfig.apiBaseUrl}/getAllConversations`;
      console.log(`🔍 conversationsService: Calling API: ${apiUrl} with limit=${limit}, offset=${offset}`);
      
      const response = await axios.get(apiUrl, {
        params: { limit, offset },
        headers: this.getAuthHeaders()
      });
      
      console.log(`📊 conversationsService: Raw API response:`, {
        total: response.data.total,
        hasMore: response.data.hasMore,
        conversationsCount: response.data.conversations?.length || 0
      });
      
      // Log conversation types from raw response
      if (response.data.conversations) {
        const rawTypes = {
          trip_status: response.data.conversations.filter((c: any) => c.conversationType === 'trip_status').length,
          check_availability: response.data.conversations.filter((c: any) => c.conversationType === 'check_availability').length,
          chat: response.data.conversations.filter((c: any) => !c.conversationType || c.conversationType === 'chat').length,
          unknown: response.data.conversations.filter((c: any) => c.conversationType && !['trip_status', 'check_availability', 'chat'].includes(c.conversationType)).length
        };
        console.log(`📊 conversationsService: Raw conversation types:`, rawTypes);
        console.log(`📋 conversationsService: First 3 raw conversations:`, response.data.conversations.slice(0, 3).map((c: any) => ({
          id: c.id?.substring(0, 8),
          type: c.conversationType || 'chat',
          createdAt: c.createdAt
        })));
      }
      
      const mappedConversations = response.data.conversations.map((conv: any) => ({
        ...conv,
        createdAt: new Date(conv.createdAt),
        updatedAt: new Date(conv.updatedAt),
        messagePreview: conv.messagePreview ? {
          ...conv.messagePreview,
          timestamp: new Date(conv.messagePreview.timestamp)
        } : undefined
      }));
      
      console.log(`✅ conversationsService: Mapped ${mappedConversations.length} conversations`);
      
      return {
        conversations: mappedConversations,
        total: response.data.total,
        hasMore: response.data.hasMore
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  async getConversationDetails(conversationId: string): Promise<Conversation> {
    try {
      const response = await axios.get(`${adminConfig.apiBaseUrl}/getConversationDetails`, {
        params: { id: conversationId },
        headers: this.getAuthHeaders()
      });
      
      return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt)
      };
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      throw error;
    }
  }

  async getMessages(conversationId: string, limit: number = 100): Promise<Message[]> {
    try {
      const response = await axios.get(`${adminConfig.apiBaseUrl}/getMessages`, {
        params: { conversationId, limit },
        headers: this.getAuthHeaders()
      });
      
      return response.data.messages.map((message: any) => ({
        ...message,
        timestamp: new Date(message.timestamp)
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }
}

export const conversationsService = new ConversationsService();

